'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherChevronRight, FeatherList } from '@subframe/core';
import { DocTituloSecao } from './DocTituloSecao';
export interface SecaoDocumento {
  numero: number;
  titulo: string;
  descricao: string;
}

/**
 * Rola até o marcador da seção. Ignora as cópias renderizadas na camada oculta de medição
 * (marcada com `data-medidor`).
 *
 * Exportada para que um sumário com layout próprio — o do documento "Autos do processo" tem
 * coluna de página — reuse a navegação em vez de duplicar a busca pelo marcador.
 */
export function irParaSecao(numero: number) {
  if (typeof document === 'undefined') return;
  const alvos = Array.from(document.querySelectorAll<HTMLElement>(`[data-secao-alvo="${numero}"]`));
  const alvo = alvos.find(el => !el.closest('[data-medidor]')) ?? alvos[0];
  // Obs.: usamos scroll instantâneo (sem 'smooth') porque o container de scroll
  // do layout não anima o scroll programático de forma confiável.
  alvo?.scrollIntoView({
    block: 'start'
  });
}
interface SumarioDocumentoProps {
  titulo: string;
  descricao: React.ReactNode;
  secoes: readonly SecaoDocumento[];
}
export function SumarioDocumento(t0) {
  const $ = _c(16);
  const {
    titulo,
    descricao,
    secoes
  } = t0;
  const totalSecoes = secoes.length;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FeatherList className="h-4 w-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== titulo) {
    t2 = <DocTituloSecao icon={t1}>{titulo}</DocTituloSecao>;
    $[1] = titulo;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== descricao) {
    t3 = <p className="mb-[14px] text-[14px] leading-[1.6] text-neutral-700">{descricao}</p>;
    $[3] = descricao;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== secoes || $[6] !== totalSecoes) {
    let t5;
    if ($[8] !== totalSecoes) {
      t5 = (secao, index) => <button key={secao.numero} type="button" onClick={() => irParaSecao(secao.numero)} className={`hover:bg-brand-50 flex w-full items-center gap-3 px-4 py-[11px] text-left transition-colors ${index < totalSecoes - 1 ? "border-b border-solid border-neutral-100" : ""}`}><span className="bg-brand-600/10 text-brand-600 flex h-[24px] w-[24px] flex-none items-center justify-center rounded-full text-[12px] font-bold">{secao.numero}</span><span className="w-[160px] flex-none text-[13px] font-bold text-neutral-800 sm:w-[240px]">{secao.titulo}</span><span className="hidden flex-1 text-[12px] text-neutral-500 sm:block">{secao.descricao}</span><FeatherChevronRight className="h-[15px] w-[15px] flex-none text-neutral-400" /></button>;
      $[8] = totalSecoes;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = secoes.map(t5);
    $[5] = secoes;
    $[6] = totalSecoes;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <div className="overflow-hidden rounded-[8px] border border-solid border-neutral-200">{t4}</div>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t5) {
    t6 = <div className="px-[37px] pt-[30px] pb-[26px]">{t2}{t3}{t5}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
interface MarcadorSecaoProps {
  numero: number;
  secoes: readonly SecaoDocumento[];
}
export function MarcadorSecao(t0) {
  const $ = _c(22);
  const {
    numero,
    secoes
  } = t0;
  let t1;
  if ($[0] !== numero || $[1] !== secoes) {
    let t2;
    if ($[3] !== numero) {
      t2 = item => item.numero === numero;
      $[3] = numero;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = secoes.find(t2);
    $[0] = numero;
    $[1] = secoes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const secao = t1;
  if (!secao) {
    return null;
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-[16px] flex items-center"><div className="bg-brand-600 h-[3px] w-[44px] flex-none rounded-full" /><div className="h-px flex-1 bg-neutral-200" /></div>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== secao.numero) {
    t3 = <span className="bg-brand-600 flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full text-[14px] font-bold text-white">{secao.numero}</span>;
    $[6] = secao.numero;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== secao.numero || $[9] !== secoes.length) {
    t4 = <div className="text-[9.5px] font-semibold tracking-[2px] text-neutral-400 uppercase">Seção {secao.numero} de {secoes.length}</div>;
    $[8] = secao.numero;
    $[9] = secoes.length;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== secao.titulo) {
    t5 = <h2 className="text-brand-600 m-0 text-[18px] leading-tight font-bold">{secao.titulo}</h2>;
    $[11] = secao.titulo;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t3 || $[17] !== t6) {
    t7 = <div className="mb-[16px] flex items-center gap-3">{t3}{t6}</div>;
    $[16] = t3;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== secao.numero || $[20] !== t7) {
    t8 = <div data-secao-alvo={secao.numero} className="scroll-mt-4 px-[37px] pt-[30px]">{t2}{t7}</div>;
    $[19] = secao.numero;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}